import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import AppCard from "@/components/apps/app-card";
import { IconoirRender } from "@/components/icons/iconoir-map";
import DashboardHeaderSection from "@/components/dashboard-header-section";
import useEnsureUserProfile from "@/hooks/use-ensure-user-profile";
import {
  fetchAeroTrakAppsByUserRole,
  addAeroTrakUserApp,
  removeAeroTrakUserApp,
  AeroTrakAppDto,
} from "@/hooks/services/aerotrak/handlers/aerotrak-apps-api";
import { toast } from "sonner";
import AppsIcon from "@mui/icons-material/Apps";

export const Route = createFileRoute("/apps")({
  component: AppsPage,
});

type App = AeroTrakAppDto;

function AppsPage() {
  const [apps, setApps] = React.useState<App[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  useEnsureUserProfile(); // ensures token retrieval side-effect

  // Fetch apps on mount
  React.useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    fetchAeroTrakAppsByUserRole()
      .then((res) => {
        if (!mounted) return;
        setApps(res.data);
        setError(null);
      })
      .catch((err) => {
        if (!mounted) return;
        const msg = err?.message || "Failed to load apps";
        setError(msg);
        toast.error(msg);
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleToggle = React.useCallback(
    async (id: number | string, next: boolean) => {
      // optimistic update
      setApps((prev) =>
        prev.map((a) => (a.id === id ? { ...a, isAssigned: next } : a))
      );
      try {
        if (next) {
          await addAeroTrakUserApp(Number(id));
          toast.success("Added to My Apps");
        } else {
          await removeAeroTrakUserApp(Number(id));
          toast.success("Removed from My Apps");
        }
      } catch (err: any) {
        // rollback
        setApps((prev) =>
          prev.map((a) => (a.id === id ? { ...a, isAssigned: !next } : a))
        );
        const msg = err?.message || "Operation failed";
        toast.error(msg);
      }
    },
    []
  );

  return (
    <>
      <div className="aero:h-full aero:w-full">
        <div className="aero:mx-auto aero:w-full aero:py-10">
          {/* Heading Section replaced with reusable component */}
          <div className="aero:mb-8">
            <DashboardHeaderSection />
          </div>

          {/* Apps Grid */}
          <section className="aero:rounded-xl aero:border aero:border-neutral-300 aero:bg-white aero:p-6">
            <div className="aero:mb-6 aero:flex aero:items-center aero:gap-3">
              <div className="aero:flex aero:h-10 aero:w-10 aero:items-center aero:justify-center aero:rounded-lg aero:bg-blue-50">
                <AppsIcon
                  className="aero:h-5 aero:w-5 aero:text-blue-800"
                  strokeWidth={2}
                />
              </div>
              <div className="aero:space-y-0.5">
                <h2 className="aero:text-base aero:font-bold aero:text-foreground">
                  Apps
                </h2>
                <p className="aero:text-sm aero:text-neutral-500">
                  Access all available applications in one place
                </p>
              </div>
            </div>
            {isLoading && (
              <div className="aero:text-sm aero:text-neutral-500">
                Loading apps...
              </div>
            )}
            {error && !isLoading && (
              <div className="aero:text-sm aero:text-red-600">{error}</div>
            )}
            {!isLoading && !error && (
              <div className="aero:grid aero:grid-cols-1 aero:gap-4 aero:sm:grid-cols-2 aero:md:grid-cols-3 aero:lg:grid-cols-5">
                {apps.map((app) => (
                  <AppCard
                    key={app.id}
                    id={app.id}
                    name={app.name}
                    description={app.description}
                    icon={
                      app.iconLibrary === "iconoir" && app.iconName ? (
                        <IconoirRender
                          name={app.iconName}
                          color={app.iconColor}
                          size={42}
                          fallback={
                            <div className="aero:text-[10px] aero:text-neutral-400">
                              NA
                            </div>
                          }
                        />
                      ) : null
                    }
                    isAssigned={app.isAssigned}
                    isAvailable={app.isAvailable}
                    onToggleAssign={handleToggle}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}

export default AppsPage;
